import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ClientEvent, SyncState, Preset, Visibility, RoomStateEvent, RoomEvent, EventType, NotificationCountType, MatrixEventEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useI18n } from '../context/I18nContext';
import { getLastActivityTimestamp, getLastConversationEvent } from '../utils/matrixEvents';
import { generateInitialsAvatar } from '../utils/avatar';
import { mergeAvatarDescriptors } from '../utils/mergeAvatarDescriptors';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';
import { GEMATIK_ROOM } from '../constants';
import matrixService from '../services/matrixService';
import { getRoomDisplayName, getRoomTopic } from '../utils/gematikRoomUtils';
import { shouldCauseReorder } from './useRoomList';
import { useIgnoredUsers } from './useIgnoredUsers';
import { toUnixMilliseconds } from '../utils/timeUtils';
import { isServerNoticesRoom } from '../utils/serverNoticesUtils';
import { getRoomPushRuleState, PushRuleState } from '../utils/pushRuleState';
import { resolveUnreadCount, isReadMarkerInTimeline, getEffectiveReadMarkerEventId } from '../utils/unreadCountUtils';
import { ensureReadMarkerInTimeline, runWithLimit } from '../utils/unreadBackfill';
import { clearFloorForRoom, wireCrossTabSync, maybeInvalidateFloorForReceipt, extractOwnReceiptTs } from '../utils/unreadFloor';

/**
 * Hook for managing public channels (public rooms)
 * Similar to useRoomList but specifically for public rooms
 *
 * @returns {Object} Object containing channel list and operations
 */
export const useChannels = () => {
  const client = useMatrixClient();
  const { t } = useI18n();
  const { getRoomAvatar, getFallbackAvatars, invalidateAvatar } = useAvatar();
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roomAvatars, setRoomAvatars] = useState({}); // roomId -> avatar descriptors array
  const [initialSyncComplete, setInitialSyncComplete] = useState(false);
  const [pushRulesVersion, setPushRulesVersion] = useState(0); // Tracks push rules changes for mute state updates
  // Bumps when m.fully_read account data arrives for any channel, so the
  // backfill effect retries rooms whose marker was null on first pass (NED-3439).
  const [accountDataVersion, setAccountDataVersion] = useState(0);
  const ignoredUsers = useIgnoredUsers(client);

  // Ref to avoid dependency cycles
  const roomAvatarsRef = useRef({});

  // Debounce timer for performance optimization (matches useRoomList pattern)
  const debounceTimerRef = useRef(null);

  // Keep ref in sync with state
  roomAvatarsRef.current = roomAvatars;


  // Create stable dependency that only changes when channel IDs change
  // Prevents effect from running on every render when channels array reference changes
  // Matches useRoomList.js pattern (line 314-316)
  const channelIds = useMemo(() => {
    return channels.map(c => c.roomId).sort().join(',');
  }, [channels]);

  /**
   * Get the last message in a channel using the shared getLastConversationEvent function.
   * This ensures consistent behavior with useRoomList for event handling.
   */
  const getLastMessage = useCallback((room, myUserId) => {
    return getLastConversationEvent(room, myUserId, t, ignoredUsers);
  }, [t, ignoredUsers]);

  /**
   * Get the last activity timestamp for a channel
   */
  const getLastActivity = useCallback((room) => {
    const membership = room.getMyMembership();

    // Defensive check for room initialization phase
    if (!room?.currentState) {
      return 0;
    }

    // For invited rooms, use the invite event timestamp so they appear at the top
    if (membership === 'invite') {
      const myUserId = client?.getUserId();
      if (myUserId) {
        const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);
        if (memberEvent) {
          const inviteTimestamp = toUnixMilliseconds(memberEvent.getTs());
          if (inviteTimestamp) return inviteTimestamp;
        }
      }
    }

    const timeline = room.getLiveTimeline();
    if (!timeline) {
      // No timeline - use stable room creation timestamp
      const createEvent = room.currentState.getStateEvents('m.room.create', '');
      if (createEvent) {
        return toUnixMilliseconds(createEvent.getTs()) ?? 0;
      }
      return 0;
    }

    const events = timeline.getEvents();

    // Get timestamp of most recent conversation activity using shared utility
    // This filters out ephemeral events (typing, receipts, presence) and
    // state-only configuration events (power_levels, encryption, etc.)
    const activityTimestamp = getLastActivityTimestamp(events);
    if (activityTimestamp) {
      return toUnixMilliseconds(activityTimestamp);
    }

    // No conversation events found - use STABLE fallback instead of getLastActiveTimestamp()
    // which can change on every sync. Use room creation time which is immutable.
    const createEvent = room.currentState.getStateEvents('m.room.create', '');
    if (createEvent) {
      return toUnixMilliseconds(createEvent.getTs()) ?? 0;
    }

    // Last resort - use getLastActiveTimestamp (unstable but better than 0)
    const fallbackTimestamp = toUnixMilliseconds(room.getLastActiveTimestamp());
    return fallbackTimestamp ?? 0;
  }, [client]);

  /**
   * Debounced update to prevent excessive re-renders from rapid Matrix events.
   * Uses 150ms delay matching useRoomList for UI responsiveness.
   * Pattern mirrors useRoomList.js lines 484-496 from Task 16.
   */
  const debouncedUpdateChannels = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setChannels(prevChannels => [...prevChannels]);
      debounceTimerRef.current = null;
    }, 150);
  }, []);

  /**
   * Handle event decryption to update channel list when encrypted messages are decrypted.
   * Without this listener, encrypted messages show <encrypted> placeholder even after
   * successful decryption. This ensures the channel list preview updates to show
   * the actual decrypted message content once decryption completes.
   *
   * OPTIMIZATION: Only processes decryption events for non-DM rooms (channels) we're actually tracking.
   * This prevents unnecessary re-renders when encrypted messages in DMs are decrypted.
   */
  const handleEventDecrypted = useCallback((event) => {
    const roomId = event.getRoomId();
    if (!roomId) return;

    // Get room object to check filters
    const room = client?.getRoom(roomId);
    if (!room) return;

    // Apply same filtering logic as updateChannels()
    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Only update if this is a non-DM room (channel) we're tracking
    if (matrixService.isDMRoom(roomId)) return;

    logger.debug(`useChannels: Event decrypted in tracked channel ${roomId}, updating channel list`);

    // Use functional setState to ensure we're checking current state
    setChannels(prevChannels => {
      // Verify channel is actually in our tracked list
      const isTracked = prevChannels.some(c => c.roomId === roomId);
      if (!isTracked) {
        // Channel passed filters but not in state yet - return unchanged
        // (Will be added via ClientEvent.Room or next sync)
        return prevChannels;
      }

      // Channel is tracked - trigger re-render for memo recalculation
      // This triggers the channelList memo to recalculate with fresh decrypted content
      return [...prevChannels];
    });
  }, [client]);

  /**
   * Handle timeline events to determine if channel list should be updated.
   * Filters for channel-specific events and uses debouncing for performance.
   * Mirrors useRoomList.handleTimelineEvent pattern from Task 16.
   */
  const handleTimelineEvent = useCallback((event, room, toStartOfTimeline) => {
    // Ignore backfill events (old messages being loaded)
    if (toStartOfTimeline) return;

    // Check if this room should be in our filtered list
    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Filter must match updateChannels() logic - only include non-DM rooms (channels)
    if (matrixService.isDMRoom(room.roomId)) return;

    // Check if this event should trigger channel reordering
    // Skip profile-only changes (avatar, displayname) to prevent unnecessary reordering
    if (!shouldCauseReorder(event)) {
      return;
    }

    logger.debug(`useChannels: Timeline event in channel ${room.roomId}, updating channel list`);

    // Use functional setState to access current state
    setChannels(prevChannels => {
      const isTracked = prevChannels.some(c => c.roomId === room.roomId);

      if (!isTracked) {
        // Channel not in list - will be added via ClientEvent.Room
        return prevChannels;
      }

      // Channel exists - trigger debounced update for memo recalculation
      debouncedUpdateChannels();
      return prevChannels;
    });
  }, [client, debouncedUpdateChannels]);

  /**
   * Handle receipt events (read receipts) to update unread badge counts.
   * Mirrors useRoomList.handleReceiptEvent pattern from Task 16 for consistency.
   */
  const handleReceiptEvent = useCallback((event, room) => {
    if (!room) return;

    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Only update channels (non-DM rooms)
    if (matrixService.isDMRoom(room.roomId)) return;

    // Cross-device read invalidation (NED-3439): see parallel comment in
    // useRoomList.handleReceiptEvent. A receipt with ts strictly newer
    // than the floor's ts means the marker moved while this device
    // wasn't actively viewing — clear the stale floor. Synth-receipt
    // replays during initial sync carry older server-side timestamps and
    // correctly do not trip this.
    const uid = client?.getUserId?.();
    if (uid) {
      const maxOwnTs = extractOwnReceiptTs(event, uid);
      if (maxOwnTs > 0) {
        maybeInvalidateFloorForReceipt(uid, room.roomId, maxOwnTs);
      }
    }

    logger.debug(`useChannels: Receipt event in channel ${room.roomId}, updating channel list`);

    setChannels(prevChannels => {
      const isTracked = prevChannels.some(c => c.roomId === room.roomId);

      if (!isTracked) {
        return prevChannels;
      }

      // Trigger debounced update
      debouncedUpdateChannels();
      return prevChannels;
    });
  }, [client, debouncedUpdateChannels]);

  /**
   * Get member count for display
   */
  const getMemberCount = useCallback((room) => {
    return room.getJoinedMemberCount();
  }, []);

  /**
   * Create a new public channel
   */
  const createChannel = useCallback(async (channelName, topic = '', inviteUsers = [], isPublic = false, initialMessage = null) => {
    if (!client) {
      throw new Error('Matrix client is not available to create a channel');
    }

    // Validate channelName - allow empty/undefined for auto-generated names
    if (channelName !== null && channelName !== undefined && typeof channelName !== 'string') {
      throw new Error('Channel name must be a string if provided');
    }

    // Validate topic parameter type
    if (topic !== null && topic !== undefined && typeof topic !== 'string') {
      throw new Error('Topic must be a string if provided');
    }

    const roomName = channelName?.trim() || '';
    const trimmedTopic = topic?.trim() || '';

    try {
      const roomOptions = {
        invite: inviteUsers,
        visibility: isPublic ? Visibility.Public : Visibility.Private,
        preset: isPublic ? Preset.PublicChat : Preset.PrivateChat,
        creation_content: {
          type: GEMATIK_ROOM.ROOM_TYPE,
        },
        initial_state: [
          {
            type: GEMATIK_ROOM.ROOM_DEFAULT,
            content: {},
            state_key: '',
          },
        ],
      };

      // Enable encryption for private channels
      if (!isPublic) {
        roomOptions.initial_state.push({
          type: EventType.RoomEncryption,
          state_key: '',
          content: { algorithm: 'm.megolm.v1.aes-sha2' },
        });
        logger.debug('Encryption enabled for private channel');
      }

      // Set history visibility to 'invited' for all channels
      roomOptions.initial_state.push({
        type: 'm.room.history_visibility',
        state_key: '',
        content: { history_visibility: 'invited' },
      });
      // Add the room name as separate state events if provided.
      // Send both Gematik custom event AND standard Matrix event for compatibility.
      // Timeline display filters to show only standard Matrix events (avoids duplicates).
      // See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
      if (roomName) {
        roomOptions.initial_state.push({
          type: GEMATIK_ROOM.ROOM_NAME,
          content: {
            name: roomName,
          },
          state_key: '',
        });
        roomOptions.initial_state.push({
          type: 'm.room.name',
          content: {
            name: roomName,
          },
          state_key: '',
        });
      }

      if(trimmedTopic) {
        roomOptions.topic = trimmedTopic;
        roomOptions.initial_state.push({
          type: GEMATIK_ROOM.ROOM_TOPIC,
          content: {
            topic: trimmedTopic,
          },
          state_key: '',
        });
      }

      logger.info(`Creating ${isPublic ? 'public' : 'private'} channel:`, roomName || '(no name)');
      const { room_id: roomId } = await client.createRoom(roomOptions);

      // Wait for the next sync to ensure the channel appears
      await new Promise(resolve => {
        const onSync = (state) => {
          if (state === SyncState.Syncing) {
            client.off(ClientEvent.Sync, onSync);
            resolve();
          }
        };
        client.on(ClientEvent.Sync, onSync);
      });

      // Send initial message if provided
      if (initialMessage && typeof initialMessage === 'string' && initialMessage.trim()) {
        logger.debug('[useChannels] Sending initial message to new channel');
        try {
          // Wait for room to be available in client
          let room = client.getRoom(roomId);
          if (!room) {
            logger.debug('[useChannels] Room not in client yet, waiting for sync...');
            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                client.off(ClientEvent.Sync, onSync);
                reject(new Error('Timeout waiting for room to sync'));
              }, 60_000); // 60 second safety timeout

              const onSync = (state) => {
                const syncedRoom = client.getRoom(roomId);
                if (syncedRoom && state === SyncState.Syncing) {
                  clearTimeout(timeout);
                  client.off(ClientEvent.Sync, onSync);
                  resolve();
                }
              };
              client.on(ClientEvent.Sync, onSync);
            });
            room = client.getRoom(roomId);
          }

          if (!room) {
            throw new Error('Room not available after creation');
          }

          const content = {
            body: initialMessage.trim(),
            msgtype: 'm.text',
          };
          await client.sendMessage(roomId, content);
          logger.debug('[useChannels] Initial message sent successfully');
        } catch (error) {
          logger.error('[useChannels] Failed to send initial message:', error);
        }
      }

      logger.info('Channel created successfully:', roomId);
      return roomId;
    } catch (error) {
      logger.error('Failed to create channel:', error);
      throw error;
    }
  }, [client]);

  /**
   * Check if the current user is a channel owner/admin
   */
  const isChannelOwner = useCallback((channelId) => {
    if (!client || !channelId) return false;

    const room = client.getRoom(channelId);
    if (!room?.currentState) return false;

    const userId = client.getUserId();

    // Check if user has power level >= 50 (moderator) or >= 100 (admin)
    const powerLevel = room.currentState.getStateEvents('m.room.power_levels', '')?.getContent();
    const userPowerLevel = powerLevel?.users?.[userId] || powerLevel?.users_default || 0;

    return userPowerLevel >= 50;
  }, [client]);

  /**
   * Effect to load and monitor channels
   */
  useEffect(() => {
    if (!client) {
      setIsLoading(true);
      setChannels([]);
      return;
    }

    const updateChannels = () => {
      // Filter rooms to include all non-DM rooms (public channels + private group chats)
      // Excludes only rooms in m.direct account data
      const nonDMRooms = client.getRooms().filter(room => {
        const membership = room.getMyMembership();

        // Include both joined and invited rooms
        if (membership !== 'join' && membership !== 'invite') {
          return false;
        }

        // Include all rooms NOT in m.direct
        // This includes: public channels, private group chats, and any non-DM rooms
        return !matrixService.isDMRoom(room.roomId);
      });

      // Use functional setState to compare with previous channels
      // Only update state if the channel IDs have actually changed
      // This prevents unnecessary memo recalculations when the same channels
      // are returned with different object references (mirrors useRoomList pattern)
      setChannels(prevChannels => {
        const prevIds = prevChannels.map(r => r.roomId).sort().join(',');
        const newIds = nonDMRooms.map(r => r.roomId).sort().join(',');
        if (prevIds === newIds) return prevChannels;
        return nonDMRooms;
      });
    };

    const handleSync = (state) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        updateChannels();
        setIsLoading(false);
      }

      // Mark initial sync as complete when we reach Syncing state
      // This enables avatar invalidation for LIVE changes only
      if (state === SyncState.Syncing && !initialSyncComplete) {
        logger.debug('[useChannels] Initial sync complete - enabling avatar invalidation');
        setInitialSyncComplete(true);
      }
    };

    const handleRoom = () => {
      updateChannels();
    };

    /**
     * Handle account data changes, specifically m.direct and m.push_rules updates.
     * This is critical because ClientEvent.Sync does NOT fire for every sync response
     * once the client is in Syncing state. When another client updates m.direct,
     * we receive a ClientEvent.AccountData event immediately, allowing us to
     * re-evaluate room grouping without waiting for state transitions.
     *
     * For m.push_rules: When push rules change (user mutes/unmutes a channel),
     * we increment pushRulesVersion to trigger channelList memo recalculation
     * with fresh muteState values.
     */
    const handleAccountData = (event) => {
      const eventType = event.getType();
      if (eventType === 'm.direct') {
        logger.debug('m.direct account data changed, updating channel list');
        updateChannels();
      } else if (eventType === 'm.ignored_user_list') {
        logger.debug('[useChannels] m.ignored_user_list changed, updating channel list');
        updateChannels();
      } else if (eventType === 'm.push_rules') {
        logger.debug('m.push_rules account data changed, updating mute states');
        setPushRulesVersion(prev => prev + 1);
      }
    };

    // Initial load
    const syncState = client.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      updateChannels();
      setIsLoading(false);

      // If already syncing when component mounts, mark initial sync as complete
      if (syncState === SyncState.Syncing) {
        logger.debug('[useChannels] Component mounted with sync already complete - enabling avatar invalidation');
        setInitialSyncComplete(true);
      }
    }

    // Room-scoped m.fully_read updates arrive as RoomEvent.AccountData, re-emitted
    // through the client. Bump accountDataVersion so the backfill effect retries
    // rooms whose marker was null on first pass (NED-3439).
    const handleRoomAccountData = (event, room) => {
      if (event?.getType?.() === 'm.fully_read') {
        // Account-data events carry no origin_server_ts, so event.getTs()
        // returns undefined. Do NOT fall back to Date.now() — that would
        // invalidate the floor on every cold-start replay of our own
        // pre-refresh marker (NED-3439). Cross-device marker advance is
        // signalled via RoomEvent.Receipt (content-level ts), not here.
        const uid = client.getUserId();
        const ts = typeof event.getTs === 'function' ? event.getTs() : undefined;
        if (uid && room?.roomId && Number.isFinite(ts)) {
          maybeInvalidateFloorForReceipt(uid, room.roomId, ts);
        }
        setAccountDataVersion(v => v + 1);
      }
    };

    // Purge persisted floor when the user leaves a channel (NED-3439 M3).
    const handleMyMembership = (room, membership) => {
      if (membership !== 'leave') return;
      const uid = client.getUserId();
      if (uid && room?.roomId) clearFloorForRoom(uid, room.roomId);
    };

    // Keep the in-memory floor cache in sync with other tabs (NED-3439 C2).
    // Mirrored in useRoomList — both hooks wire the listener independently so
    // cross-tab sync keeps working if either hook is mounted on its own.
    // The underlying listener is keyed by userId and is idempotent enough to
    // tolerate both hooks installing it concurrently.
    const teardownCrossTabSync = wireCrossTabSync(client.getUserId());

    // Set up listeners
    client.on(ClientEvent.Sync, handleSync);
    client.on(ClientEvent.Room, handleRoom);
    client.on(ClientEvent.AccountData, handleAccountData); // For m.direct changes
    client.on(RoomEvent.AccountData, handleRoomAccountData); // For m.fully_read (NED-3439)
    client.on(RoomEvent.MyMembership, handleMyMembership); // Purge floor on leave (NED-3439)
    client.on(MatrixEventEvent.Decrypted, handleEventDecrypted); // For encrypted message decryption
    client.on(RoomEvent.Timeline, handleTimelineEvent); // For timeline message updates
    client.on(RoomEvent.Receipt, handleReceiptEvent); // For read receipt updates

    return () => {
      // Cleanup debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // REMOVED: Blob URL cleanup - avatarManager now handles all blob URL lifecycle
      // Direct revocation here caused race conditions where URLs were revoked while still in use
      // for (const blobUrl of blobUrlsRef.current.values()) {
      //   if (blobUrl && blobUrl.startsWith('blob:')) {
      //     URL.revokeObjectURL(blobUrl);
      //   }
      // }
      // blobUrlsRef.current.clear();

      teardownCrossTabSync();
      client.off(ClientEvent.Sync, handleSync);
      client.off(ClientEvent.Room, handleRoom);
      client.off(ClientEvent.AccountData, handleAccountData);
      client.off(RoomEvent.AccountData, handleRoomAccountData);
      client.off(RoomEvent.MyMembership, handleMyMembership);
      client.off(MatrixEventEvent.Decrypted, handleEventDecrypted);
      client.off(RoomEvent.Timeline, handleTimelineEvent);
      client.off(RoomEvent.Receipt, handleReceiptEvent);
    };
  }, [client, handleEventDecrypted, handleTimelineEvent, handleReceiptEvent]);

  /**
   * Backfill the live timeline of tracked channels whose read marker has
   * been paginated out, so `resolveUnreadCount` can compute a trusted
   * marker-based count instead of the decryption-driven SDK count crawl
   * (NED-3439 cold-start refresh).
   */
  useEffect(() => {
    if (!client || !channels || channels.length === 0) return undefined;
    const myUserId = client.getUserId();
    if (!myUserId) return undefined;

    let cancelled = false;

    const candidates = channels.filter((room) => {
      try {
        const marker = getEffectiveReadMarkerEventId(room, myUserId);
        if (!marker) return false;
        return !isReadMarkerInTimeline(room, myUserId);
      } catch {
        return false;
      }
    });

    if (candidates.length === 0) return undefined;

    const tasks = candidates.map((room) => async () => {
      const marker = getEffectiveReadMarkerEventId(room, myUserId);
      if (!marker) return;
      const result = await ensureReadMarkerInTimeline(client, room, marker);
      if (cancelled) return;
      // Skip when the outcome kind is unchanged — avoids the 150ms render
      // churn during the BACKOFF_MS cap-out window (NED-3439 Workstream B).
      if ((result.found || result.cappedOut) && !result.alreadyEmitted) {
        debouncedUpdateChannels();
      }
    });

    runWithLimit(tasks, 4).catch((err) => {
      logger.warn('[useChannels] backfill runner error', { error: err?.message || String(err) });
    });

    return () => { cancelled = true; };
  }, [client, channels, accountDataVersion, debouncedUpdateChannels]);

  // Listen for room name changes to refresh channel list display names (NED-2711)
  // RoomEvent.Name fires when the SDK's calculated room name changes. The Gematik
  // custom event (de.gematik.tim.room.name) is always dual-written alongside
  // m.room.name, so RoomEvent.Name alone is sufficient.
  useEffect(() => {
    if (!client || !initialSyncComplete) return;

    const handleRoomNameChange = (room) => {
      if (matrixService.isDMRoom(room.roomId)) return;
      const membership = room.getMyMembership();
      if (membership !== 'join' && membership !== 'invite') return;
      debouncedUpdateChannels();
    };

    client.on(RoomEvent.Name, handleRoomNameChange);

    return () => {
      client.off(RoomEvent.Name, handleRoomNameChange);
    };
  }, [client, initialSyncComplete, debouncedUpdateChannels]);

  /**
   * Fetch room avatars for channels using avatarManager
   *
   * avatarManager now supports both user and room avatars using the same
   * caching infrastructure, reference counting, and IndexedDB storage.
   */
  useEffect(() => {
    if (!client || !channels.length) {
      return;
    }

    let isMounted = true; // Guard against state updates after unmount

    const fetchRoomAvatars = async () => {
      // Filter out rooms whose avatars we already have to avoid redundant fetches
      const roomsNeedingAvatars = channels.filter(
        room => !roomAvatarsRef.current[room.roomId],
      );

      if (roomsNeedingAvatars.length === 0) {
        return; // All avatars already loaded
      }

      // Fetch all room avatars in parallel via context
      const results = await Promise.all(
        roomsNeedingAvatars.map(async (room) => {
          try {
            // Try explicit room avatar first
            const explicitAvatar = await getRoomAvatar(
              room.roomId,
              room.name || 'Channel',
              40,
            );

            if (explicitAvatar) {
              // Room has explicit avatar
              return { roomId: room.roomId, avatars: [{ url: explicitAvatar, type: 'room' }] };
            } else {
              // No explicit avatar - get fallback member avatars
              const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
              return {
                roomId: room.roomId,
                avatars: fallbackAvatars.map(desc => ({
                  url: desc.url,
                  type: 'user',
                })),
              };
            }
          } catch (error) {
            logger.warn(`Failed to fetch avatars for channel ${room.roomId}:`, error);
            return { roomId: room.roomId, avatars: [] };
          }
        }),
      );

      // Batch update room avatars state only if component is still mounted
      if (isMounted) {
        const newAvatars = {};
        results.forEach(result => {
          if (result && result.avatars) {
            newAvatars[result.roomId] = result.avatars;
          }
        });

        if (Object.keys(newAvatars).length > 0) {
          setRoomAvatars(prev => mergeAvatarDescriptors(prev, newAvatars));
        }
      }
    };

    fetchRoomAvatars();

    // Cleanup: only prevent state updates after unmount
    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, channelIds, getRoomAvatar, getFallbackAvatars]);
  // Note: Using channelIds instead of channels to prevent effect from running on every render
  // Effect only runs when rooms are actually added/removed, not when array reference changes

  /**
   * Listen for room avatar changes and update in real-time
   */
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleRoomAvatarChange = async (event) => {
      // Filter for room avatar events only
      const eventType = event.getType();

      if (eventType !== EventType.RoomAvatar) return;

      const changedRoomId = event.getRoomId();

      // Check if this is one of our channels using live client.getRooms() to avoid stale closure
      // This fixes race condition where avatar is set before effect re-runs with new channels array
      const allRooms = client.getRooms();
      const channelRooms = allRooms.filter(room => {
        const membership = room.getMyMembership();
        return (membership === 'join' || membership === 'invite') && !matrixService.isDMRoom(room.roomId);
      });
      const isChannel = channelRooms.some(room => room.roomId === changedRoomId);

      if (!isChannel) {
        return; // Not a channel we're tracking
      }

      logger.debug(`useChannels: Room avatar changed for ${changedRoomId}`);

      try {
        const room = client.getRoom(changedRoomId);
        if (!room) {
          logger.warn(`useChannels: Room ${changedRoomId} not found`);
          return;
        }

        // CRITICAL FIX: Get MXC URL from event content instead of room state
        // The event contains the NEW avatar URL, while room.getMxcAvatarUrl()
        // may return stale data because SDK hasn't updated room state yet
        const eventContent = event.getContent();
        const newAvatarMxc = eventContent?.url || null;

        // Invalidate cache via context (clears old avatar from avatarManager)
        await invalidateAvatar(changedRoomId);

        let avatarDescriptors;

        if (newAvatarMxc) {
          // Avatar was set - use avatarManager to fetch via context
          // avatarManager handles blob URL creation, caching, and lifecycle
          try {
            const roomName = room?.name || 'Channel';

            const blobUrl = await getRoomAvatar(changedRoomId, roomName, 40);

            if (blobUrl) {
              avatarDescriptors = [{ url: blobUrl, type: 'room' }];
            } else {
              // getRoomAvatar returned null - fallback to member avatars
              const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
              avatarDescriptors = fallbackAvatars.map(desc => ({
                url: desc.url,
                type: 'user',
              }));
            }
          } catch (fetchError) {
            logger.warn(`Failed to fetch room avatar for ${changedRoomId}:`, fetchError);
            // Fall back to member avatars
            const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
            avatarDescriptors = fallbackAvatars.map(desc => ({
              url: desc.url,
              type: 'user',
            }));
          }
        } else {
          // Avatar was removed (url is null/empty) - use fallback member avatars
          const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
          avatarDescriptors = fallbackAvatars.map(desc => ({
            url: desc.url,
            type: 'user',
          }));
        }

        // Update state with new avatars
        setRoomAvatars(prev => ({
          ...prev,
          [changedRoomId]: avatarDescriptors,
        }));

        // Update ref cache to match state
        roomAvatarsRef.current[changedRoomId] = avatarDescriptors;

        logger.debug(`useChannels: Room avatar updated for ${changedRoomId}`);
      } catch (error) {
        logger.warn(`useChannels: Error updating room avatar for ${changedRoomId}:`, error);
        // On error, set empty array to fall back to initials
        setRoomAvatars(prev => ({
          ...prev,
          [changedRoomId]: [],
        }));
      }
    };

    client.on(RoomStateEvent.Events, handleRoomAvatarChange);

    logger.debug('useChannels: Room avatar change listener registered');

    return () => {
      client.off(RoomStateEvent.Events, handleRoomAvatarChange);
      logger.debug('useChannels: Room avatar change listener removed');
    };
  }, [client, invalidateAvatar, getRoomAvatar, getFallbackAvatars]);

  /**
   * Listen for member profile changes (avatar updates) in channels
   * This handles when a user changes their avatar and we need to update
   * the conversation list to reflect the new avatar.
   *
   * CRITICAL: Uses initialSyncComplete flag to skip historical avatar changes
   * during initial sync, preventing race conditions where avatars get invalidated
   * before they finish caching.
   */
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleMemberProfileChange = async (event, room, toStartOfTimeline) => {
      // Ignore backfill events
      if (toStartOfTimeline) return;

      // Only process m.room.member events
      if (event.getType() !== 'm.room.member') return;

      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check if avatar_url changed
      if (content.avatar_url !== prevContent?.avatar_url) {
        const changedUserId = event.getStateKey();

        // CRITICAL FIX: Skip invalidation during initial sync
        // During initial sync, Matrix replays historical member events (including avatar changes).
        // We don't need to invalidate anything because:
        // 1. Nothing is cached yet - channel list will fetch current avatar state directly
        // 2. Historical intermediate states don't matter - only final state matters
        // 3. This prevents race condition where fresh avatars are invalidated immediately after caching
        // After initial sync completes, we process LIVE avatar changes normally.
        if (!initialSyncComplete) {
          logger.debug('[useChannels] Skipping avatar update during initial sync', {
            userId: changedUserId,
            roomId: room?.roomId,
          });
          return;
        }

        logger.debug('[useChannels] Member avatar URL changed', {
          userId: changedUserId,
          roomId: room?.roomId,
          oldUrl: prevContent?.avatar_url,
          newUrl: content.avatar_url,
        });

        // Check if this is one of our channels
        const isChannel = channels.some(channel => channel.roomId === room?.roomId);
        if (!isChannel) {
          logger.debug('[useChannels] Room not in channels list, skipping');
          return;
        }

        try {
          // Invalidate the user's avatar cache
          await invalidateAvatar(changedUserId);
          logger.debug('[useChannels] Invalidated avatar cache for user', { userId: changedUserId });

          // FIX: Don't delete ref cache - we'll update it after fetching fresh fallback avatars

          // For channels with explicit room avatar, no need to update
          // (member avatars only used for fallback)
          const roomObj = client.getRoom(room.roomId);
          if (!roomObj) return;

          const explicitAvatar = await getRoomAvatar(
            room.roomId,
            roomObj.name || 'Channel',
            40,
          );

          if (explicitAvatar) {
            logger.debug('[useChannels] Room has explicit avatar, no fallback update needed');
            return; // Room has its own avatar, don't update
          }

          // Re-fetch fallback avatars since a member's avatar changed
          const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
          const avatarDescriptors = fallbackAvatars.map(desc => ({
            url: desc.url,
            type: 'user',
          }));

          // Update state with new avatars
          setRoomAvatars(prev => ({
            ...prev,
            [room.roomId]: avatarDescriptors,
          }));

          // FIX: Update ref cache to match state (instead of deleting it)
          roomAvatarsRef.current[room.roomId] = avatarDescriptors;

          logger.debug('[useChannels] Updated fallback avatars for room', {
            roomId: room.roomId,
            avatarCount: avatarDescriptors.length,
          });
        } catch (error) {
          logger.warn('[useChannels] Error updating member avatar:', error);
        }
      }
    };

    client.on(RoomEvent.Timeline, handleMemberProfileChange);

    logger.debug('[useChannels] Member profile change listener registered');

    return () => {
      client.off(RoomEvent.Timeline, handleMemberProfileChange);
      logger.debug('[useChannels] Member profile change listener removed');
    };
  }, [client, channels, getRoomAvatar, getFallbackAvatars, invalidateAvatar, initialSyncComplete]);

  // Listen for server notices room avatar refresh events
  // This handles the case where a server notices room transitions from invite→join
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleServerNoticesAvatarRefresh = async (event) => {
      const { roomId } = event.detail || {};
      if (!roomId) return;

      // Check if this is one of our channels
      const isChannel = channels.some(channel => channel.roomId === roomId);
      if (!isChannel) return;

      logger.info(`[useChannels] Server notices avatar refresh triggered for ${roomId}`);

      try {
        const room = client.getRoom(roomId);
        if (!room) return;

        const roomName = getRoomDisplayName(room);
        
        // Force refresh the avatar (server notices should return the special SVG)
        const avatar = await getRoomAvatar(roomId, roomName, 40);
        
        if (avatar) {
          setRoomAvatars(prev => ({
            ...prev,
            [roomId]: [{ url: avatar, type: 'room' }],
          }));
          logger.info(`[useChannels] Server notices avatar refreshed: ${roomId}`);
        }
      } catch (error) {
        logger.error('[useChannels] Failed to refresh server notices avatar:', error);
      }
    };

    window.addEventListener('matrix:room:avatar:refresh', handleServerNoticesAvatarRefresh);

    return () => {
      window.removeEventListener('matrix:room:avatar:refresh', handleServerNoticesAvatarRefresh);
    };
  }, [client, channels, getRoomAvatar]);

  /**
   * Process channels into display format
   */
  const channelList = useMemo(() => {
    if (isLoading || !channels.length) {
      return [];
    }

    const myUserId = client?.getUserId();

    return channels.map(room => {
      const lastActivity = getLastActivity(room);
      const membership = room.getMyMembership();

      // Get channel name considering Gematik custom name
      const channelName = getRoomDisplayName(room);
      const topic = getRoomTopic(room);

      // Extract avatar URLs from descriptors, filtering out any undefined/null entries
      const descriptors = roomAvatars[room.roomId];
      let avatarUrls = [];

      if (Array.isArray(descriptors)) {
        avatarUrls = descriptors
          .filter(descriptor => {
            // Ensure descriptor is an object with a url property
            if (!descriptor || typeof descriptor !== 'object') {
              return false;
            }
            return descriptor.url && typeof descriptor.url === 'string';
          })
          .map(descriptor => descriptor.url);
      }

      // Fallback to initials if no avatars
      if (avatarUrls.length === 0) {
        avatarUrls = [generateInitialsAvatar(channelName || 'CH')];
      }

      // Get mute state from push rules (must be before unreadCount for conditional counting)
      const muteState = getRoomPushRuleState(client, room.roomId);
      const isMuted = muteState !== PushRuleState.NOTIFY;

      // For muted rooms, SDK notification count is always 0 (server doesn't count when
      // push rules suppress). resolveUnreadCount handles the fallback to client-side
      // counting with a module-level cache to survive component remounts. (NED-3296)
      const unreadCount = resolveUnreadCount(room, myUserId, isMuted);
      const highlightCount = room.getUnreadNotificationCount(NotificationCountType.Highlight);

      // Check if the channel is encrypted by looking for m.room.encryption state event
      const encryptionEvent = room.currentState.getStateEvents('m.room.encryption', '');
      const isEncrypted = !!encryptionEvent;

      // Check if the channel is public by looking for m.room.join_rules state event
      const joinRulesEvent = room.currentState.getStateEvents('m.room.join_rules', '');
      const isPublic = joinRulesEvent?.getContent()?.join_rule === 'public';

      // Server notices rooms should never show as invites (auto-joined via useServerNoticesAutoJoin)
      const isServerNotices = isServerNoticesRoom(room, client);

      return {
        id: room.roomId,
        name: channelName || 'Unnamed Channel',
        title: channelName || 'Unnamed Channel', // Add title for SideBar compatibility
        topic,
        lastMessage: getLastMessage(room, myUserId),
        lastActivity,
        memberCount: getMemberCount(room),
        avatar: avatarUrls[0], // Keep single avatar for other uses
        avatars: avatarUrls, // Provide full array for ConversationList
        isOwner: isChannelOwner(room.roomId),
        unreadCount,
        isInvite: membership === 'invite' && !isServerNotices,
        isEncrypted,
        isPublic,
        muteState,
        isMuted,
      };
    });
  }, [client, channels, isLoading, getLastMessage, getLastActivity, getMemberCount, isChannelOwner, roomAvatars, pushRulesVersion]);

  return {
    channelList,
    isLoading,
    createChannel,
    isChannelOwner,
  };
};

export default useChannels;
